// ════════════════════════════════════════════════════════════════════════════
//  ManDana — where a manifestation keeps its own rows
//  ---------------------------------------------------------------------------
//  A database manifestation needs somewhere to put its data, and records are the
//  wrong shape and the wrong cost for it. A reading list, a CRM, a habit grid —
//  none of those are things that happened on a day, and a row written per
//  keystroke into a day document would re-stamp and re-push that whole document
//  every time somebody typed.
//
//  ── ONE OPAQUE BLOB PER INSTANCE, AND DELIBERATELY NOTHING MORE ────────────
//  Not rows, not a query language, not an index. `get` hands back whatever was
//  last `put`, and the manifestation chooses its own shape. That is a smaller
//  promise than it looks and it is on purpose: a query API here would be a
//  second data model to keep in step with the first, and this one has exactly
//  one failure mode we can name out loud — too large.
//
//  ── A NEW DATABASE, NOT A NEW STORE IN mandana-files ───────────────────────
//  Adding a store means bumping DB_VERSION on the database that holds the only
//  copy of somebody's PDFs, and running an upgrade transaction against it, for a
//  feature that does not need to be anywhere near those bytes. A migration with
//  no upside is a migration that can only cost.
//
//  ── KEYED ON THE INSTANCE ──────────────────────────────────────────────────
//  Not on the content hash. That is what lets somebody drop version two of a
//  dashboard onto the same portal and find their rows still there — the code
//  changed, the thing it belongs to did not. md-grant.ts makes the matching
//  decision from the other side: the PERMISSION does not survive that, because
//  the code is what was permitted, but the DATA does, because it never was
//  anybody else's business.
//
//  ── DEVICE-LOCAL, LIKE THE BYTES IT BELONGS TO ─────────────────────────────
//  This does not sync, and it does not need its own argument for that: the
//  manifestation's own bytes do not sync either (md-file.ts), so data that
//  travelled would arrive on a device with nothing to open it. Both are waiting
//  on the same answer.
// ════════════════════════════════════════════════════════════════════════════

(function (g: any) {
  'use strict';

  const DB_NAME = 'mandana-manifests';
  const DB_VERSION = 1;
  const STORE = 'data';

  /* Resolved once and remembered for the rest of the page load, never
     recomputed. Adoption decides which side of the legacy/scoped line this
     account fell on at boot, before anything opens a database — a name that
     could change mid-session would be a device that loses track of which rows
     belong to which manifestation partway through opening the store.

     Called defensively: md-adopt.ts loads before this module in the app, but
     not in every test harness, and the identity default — the bare name — is
     exactly right for a page that has not decided otherwise yet. */
  let dbName: string | null = null;
  function nameOf(): string {
    if (dbName === null) {
      const asked: unknown = (typeof g.mdCompartmentDbName === 'function')
        ? g.mdCompartmentDbName(DB_NAME) : DB_NAME;
      dbName = typeof asked === 'string' && asked ? asked : DB_NAME;
    }
    return dbName;
  }

  /* Generous for anything hand-written, small enough that the whole blob can be
     rewritten on every change without anybody noticing. Whole-blob rewrite is
     what buys the "no query language" simplicity above; this is the number that
     keeps that honest. */
  const MD_MSTORE_MAX_BYTES = 2 * 1024 * 1024;

  const ID_RE = /^[a-z0-9]{1,64}$/;

  /** Turn a value into what will be stored, or name why it cannot be.

      Pure, and separated from the database for the same reason md-file.ts keeps
      its hash and its naming pure: these are the two answers that have to be
      assertable without opening IndexedDB, and they are the two that a
      manifestation can actually trigger by mistake. */
  function mdMStoreSerialise(value: unknown): { ok: true; text: string } | { ok: false; error: string } {
    let text: string;
    try {
      text = JSON.stringify(value === undefined ? null : value) as string;
    } catch (e) {
      /* A cycle, or a value structured-clone accepted and JSON will not — a
         function, a DOM node, a BigInt. Named rather than thrown, because the
         caller is a message handler answering a frame. */
      return { ok: false, error: 'bad_arg' };
    }
    if (typeof text !== 'string') return { ok: false, error: 'bad_arg' };
    if (text.length > MD_MSTORE_MAX_BYTES) return { ok: false, error: 'too_large' };
    return { ok: true, text };
  }

  /* Open and close around every call, exactly as md-file.ts does and for the
     same reason: holding a connection open means handling `versionchange` from
     another tab, for no gain. */
  function open(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!g.indexedDB) { reject(new Error('no indexedDB')); return; }
      const rq = g.indexedDB.open(nameOf(), DB_VERSION);
      rq.onupgradeneeded = () => {
        const db = rq.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      rq.onsuccess = () => resolve(rq.result);
      rq.onerror = () => reject(rq.error);
    });
  }

  function wrap(rq: any): Promise<any> {
    return new Promise((resolve, reject) => {
      rq.onsuccess = () => resolve(rq.result);
      rq.onerror = () => reject(rq.error);
    });
  }

  /** What this manifestation last stored, or null. Null is ordinary — it is what
      a manifestation opening for the first time sees. */
  async function mdMStoreGet(instance: unknown): Promise<unknown> {
    if (typeof instance !== 'string' || !ID_RE.test(instance)) return null;
    try {
      const db = await open();
      const raw = await wrap(db.transaction(STORE, 'readonly').objectStore(STORE).get(instance));
      db.close();
      if (typeof raw !== 'string') return null;
      try { return JSON.parse(raw); } catch (e) { return null; }
    } catch (e) {
      return null;
    }
  }

  /** Replace what this manifestation stores. Never throws. */
  async function mdMStorePut(instance: unknown, value: unknown): Promise<{ ok: boolean; error?: string }> {
    if (typeof instance !== 'string' || !ID_RE.test(instance)) return { ok: false, error: 'bad_arg' };
    const s = mdMStoreSerialise(value);
    if (!s.ok) return { ok: false, error: (s as { error: string }).error };
    try {
      const db = await open();
      await wrap(db.transaction(STORE, 'readwrite').objectStore(STORE).put((s as { text: string }).text, instance));
      db.close();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: 'internal' };
    }
  }

  /** Forget it — for when a manifestation is removed from the library. */
  async function mdMStoreDelete(instance: unknown): Promise<boolean> {
    if (typeof instance !== 'string' || !ID_RE.test(instance)) return false;
    try {
      const db = await open();
      await wrap(db.transaction(STORE, 'readwrite').objectStore(STORE).delete(instance));
      db.close();
      return true;
    } catch (e) {
      return false;
    }
  }

  g.MD_MSTORE_MAX_BYTES = MD_MSTORE_MAX_BYTES;
  g.mdMStoreSerialise = mdMStoreSerialise;
  g.mdMStoreGet = mdMStoreGet;
  g.mdMStorePut = mdMStorePut;
  g.mdMStoreDelete = mdMStoreDelete;

  // CommonJS escape hatch, following md-file.ts and md-grant.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { MD_MSTORE_MAX_BYTES, mdMStoreSerialise, mdMStoreGet,
      mdMStorePut, mdMStoreDelete };
  }
})(typeof window !== 'undefined' ? window : globalThis);
